import React, { useState, useEffect } from "react";
import { AnimatedProgressBar } from "@dreambigger/design-system/src";

interface LoadingMessage {
  text: string;
  icon: React.ComponentType<any>;
}

interface ProgressiveLoadingProps {
  className?: string;
  loadingMessages: LoadingMessage[];
  messageInterval?: number;
  progressDuration?: number;
  loop?: boolean;
}

const ProgressiveLoading: React.FC<ProgressiveLoadingProps> = ({
  className = "",
  loadingMessages,
  messageInterval = 5000,
  progressDuration = 15000,
  loop = false,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoopCompleted, setIsLoopCompleted] = useState(false);

  useEffect(() => {
    // Progress bar fills to match progressDuration with random increments
    const intervalDuration = 100; // Fixed 100ms intervals
    const totalIntervals = progressDuration / intervalDuration;
    let currentInterval = 0;

    const progressInterval = setInterval(() => {
      setProgress(() => {
        currentInterval++;

        // Calculate base progress based on time elapsed
        const baseProgress = (currentInterval / totalIntervals) * 100;

        // Add random variance while staying near the time-based progress
        const variance = Math.random() * 10 - 5; // ±5% variance
        const newProgress = Math.min(100, Math.max(0, baseProgress + variance));

        // Don't exceed 100% before the duration is complete
        if (currentInterval >= totalIntervals) {
          return 100;
        }

        return newProgress;
      });
    }, intervalDuration);

    // Message rotation at specified interval with transition
    const msgInterval = setInterval(() => {
      if (isLoopCompleted) {
        return; // Stop processing if loop is completed
      }

      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => {
          const nextIndex = prev + 1;
          if (loop) {
            return nextIndex % loadingMessages.length;
          } else {
            if (nextIndex >= loadingMessages.length) {
              setIsLoopCompleted(true);
              return prev; // Stay at last message
            }
            return nextIndex;
          }
        });
        setIsTransitioning(false);
      }, 200); // Half the transition duration
    }, messageInterval);

    return () => {
      clearInterval(progressInterval);
      clearInterval(msgInterval);
    };
  }, [
    loadingMessages.length,
    messageInterval,
    progressDuration,
    loop,
    isLoopCompleted,
  ]);

  // Clear interval when loop is completed
  useEffect(() => {
    if (isLoopCompleted) {
      // The interval will naturally stop checking due to the guard clause above
    }
  }, [isLoopCompleted]);

  // Ensure progress completes within the specified duration
  useEffect(() => {
    const completionTimer = setTimeout(() => {
      setProgress(100);
    }, progressDuration);

    return () => clearInterval(completionTimer);
  }, [progressDuration]);

  const currentMessage = loadingMessages[currentMessageIndex];
  const IconComponent = currentMessage.icon;

  return (
    <div className={`tc pa-8 ${className}`}>
      <div className="mb-6">
        <AnimatedProgressBar className="br-2" progress={Math.round(progress)} />

        <div
          className={`f-5 mt-4 tc flex items-center justify-center transition-all duration-400 ${
            isTransitioning ? "o-0" : "o-100"
          }`}
          style={{
            transform: isTransitioning ? "translateY(-10px)" : "translateY(0)",
            transition: "opacity 0.4s ease-in-out, transform 0.4s ease-in-out",
          }}
        >
          <IconComponent
            className="mr-2 primary"
            style={{
              fontSize: "20px",
            }}
          />
          {currentMessage.text}
        </div>
      </div>
    </div>
  );
};

export default ProgressiveLoading;
